import { forwardRef, InputHTMLAttributes, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

import { entryColors, entryFont, entryLayout } from "./tokens";

/* ------------------------------------------------------------------ */
/* Typography                                                          */
/* ------------------------------------------------------------------ */

export const EntryTitle = styled.h1<{ $align?: "left" | "center"; $size?: number }>`
  margin: 0;
  font-family: ${entryFont.title};
  font-weight: 400;
  font-size: ${({ $size }) => $size ?? 38}px;
  line-height: 1.28;
  color: ${entryColors.textPrimary};
  text-align: ${({ $align }) => $align ?? "left"};
  letter-spacing: 0.2px;

  b,
  strong {
    font-weight: 700;
  }

  @media (max-width: 640px) {
    font-size: ${({ $size }) => Math.round(($size ?? 38) * 0.72)}px;
  }
`;

export const EntryBody = styled.p<{ $align?: "left" | "center" }>`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${entryColors.textSecondary};
  text-align: ${({ $align }) => $align ?? "left"};
`;

export const EntryBodyStrong = styled(EntryBody)`
  font-weight: 600;
  color: ${entryColors.textPrimary};
`;

export const EntryLink = styled.a`
  color: ${entryColors.highlight};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: ${entryColors.textPrimary};
  animation: ${spin} 0.7s linear infinite;
`;

type CtaVariant = "terracotta" | "green" | "glass";

const ctaBackground = (variant: CtaVariant, disabled?: boolean) => {
  if (disabled) {
    return css`
      background: rgba(180, 180, 190, 0.35);
      border: 1px solid rgba(255, 255, 255, 0.14);
      color: rgba(255, 255, 255, 0.7);
      cursor: default;
    `;
  }

  switch (variant) {
    case "terracotta":
      return css`
        background: linear-gradient(
          180deg,
          ${entryColors.terracotta} 0%,
          ${entryColors.terracottaDeep} 100%
        );
        border: 1px solid rgba(255, 255, 255, 0.18);
      `;
    case "green":
      return css`
        background: linear-gradient(180deg, ${entryColors.green} 0%, ${entryColors.greenDeep} 100%);
        border: 1px solid rgba(255, 255, 255, 0.18);
      `;
    case "glass":
      return css`
        background: ${entryColors.glassFillStrong};
        border: 1px solid ${entryColors.glassBorder};
      `;
  }
};

export const EntryCta = styled.button<{
  $variant?: CtaVariant;
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: ${entryLayout.ctaWidth}px;
  height: ${entryLayout.ctaHeightPx}px;
  border-radius: ${entryLayout.radiusPill}px;
  font-family: ${entryFont.body};
  font-size: 16px;
  font-weight: 700;
  color: ${entryColors.textPrimary};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
  transition:
    transform 160ms ease,
    filter 160ms ease,
    box-shadow 160ms ease;
  ${({ $variant = "terracotta", $disabled }) => ctaBackground($variant, $disabled)}

  ${({ $disabled }) =>
    !$disabled &&
    css`
      cursor: pointer;

      &:hover {
        filter: brightness(1.08);
        transform: translateY(-1px);
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.34);
      }

      &:active {
        transform: translateY(0) scale(0.985);
        filter: brightness(0.97);
      }
    `}

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 3px;
  }
`;

export const EntryGhostCta = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: ${entryLayout.ctaWidth}px;
  height: ${entryLayout.ctaHeightPx}px;
  background: transparent;
  border: none;
  font-family: ${entryFont.body};
  font-size: 16px;
  font-weight: 600;
  color: ${entryColors.textPrimary};
  cursor: pointer;
  border-radius: ${entryLayout.radiusPill}px;
  transition: background 160ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 3px;
  }
`;

/* ------------------------------------------------------------------ */
/* Glass input                                                         */
/* ------------------------------------------------------------------ */

const Field = styled.div<{ $error?: boolean }>`
  position: relative;
  width: 100%;
  border-radius: ${entryLayout.radiusInput}px;
  background: ${entryColors.glassFill};
  border: 1px solid ${({ $error }) => ($error ? entryColors.error : entryColors.glassBorder)};
  backdrop-filter: blur(14px) saturate(1.15);
  -webkit-backdrop-filter: blur(14px) saturate(1.15);
  transition:
    border-color 180ms ease,
    background 180ms ease;

  &:focus-within {
    border-color: ${({ $error }) => ($error ? entryColors.error : entryColors.glassBorderFocus)};
    background: ${entryColors.glassFillStrong};
  }
`;

const FieldLabel = styled.label`
  display: block;
  padding: 12px 20px 0;
  font-family: ${entryFont.body};
  font-size: 13px;
  font-weight: 500;
  color: ${entryColors.textSecondary};
`;

const FieldInput = styled.input<{ $hasLabel?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  background: transparent;
  border: none;
  outline: none;
  padding: ${({ $hasLabel }) => ($hasLabel ? "6px 20px 14px" : "22px 20px")};
  font-family: ${entryFont.body};
  font-size: 18px;
  color: ${entryColors.textPrimary};
  caret-color: ${entryColors.highlight};

  &::placeholder {
    color: ${entryColors.textTertiary};
  }

  /* Keep the glass look when the browser autofills. */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${entryColors.textPrimary};
    -webkit-box-shadow: 0 0 0 1000px rgba(30, 28, 40, 0.9) inset;
    transition: background-color 9999s ease-out;
  }
`;

const FieldError = styled.span`
  display: block;
  margin-top: 6px;
  font-family: ${entryFont.body};
  font-size: 13px;
  color: ${entryColors.error};
`;

type GlassInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  error?: string;
};

export const GlassInput = forwardRef<HTMLInputElement, GlassInputProps>(
  ({ label, error, id, ...inputProps }, ref) => (
    <div style={{ width: "100%" }}>
      <Field $error={Boolean(error)}>
        {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
        <FieldInput ref={ref} id={id} $hasLabel={Boolean(label)} {...inputProps} />
      </Field>
      {error ? <FieldError role="alert">{error}</FieldError> : null}
    </div>
  ),
);

GlassInput.displayName = "GlassInput";

/* ------------------------------------------------------------------ */
/* Glass select (custom dropdown matching the glass aesthetic)         */
/* ------------------------------------------------------------------ */

const SelectTrigger = styled.button<{ $open?: boolean; $hasValue?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border-radius: ${entryLayout.radiusInput}px;
  background: ${entryColors.glassFill};
  border: 1px solid
    ${({ $open }) => ($open ? entryColors.glassBorderFocus : entryColors.glassBorder)};
  backdrop-filter: blur(14px) saturate(1.15);
  -webkit-backdrop-filter: blur(14px) saturate(1.15);
  padding: 0 20px;
  height: 64px;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 180ms ease,
    background 180ms ease;

  &:hover {
    background: ${entryColors.glassFillStrong};
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

const SelectLabel = styled.span<{ $floated?: boolean }>`
  position: absolute;
  left: 20px;
  font-family: ${entryFont.body};
  color: ${entryColors.textSecondary};
  pointer-events: none;
  transition:
    transform 180ms ease,
    font-size 180ms ease;
  transform-origin: left center;
  font-size: ${({ $floated }) => ($floated ? "13px" : "18px")};
  font-weight: 500;
  transform: ${({ $floated }) => ($floated ? "translateY(-12px)" : "translateY(0)")};
`;

const SelectValue = styled.span`
  font-family: ${entryFont.body};
  font-size: 18px;
  color: ${entryColors.textPrimary};
  padding-top: 12px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectChevron = styled.span<{ $open?: boolean }>`
  flex-shrink: 0;
  margin-left: auto;
  display: inline-flex;
  transition: transform 200ms ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 260px;
  overflow-y: auto;
  border-radius: ${entryLayout.radiusInput}px;
  background: rgba(22, 22, 38, 0.92);
  border: 1px solid ${entryColors.glassBorder};
  backdrop-filter: blur(24px) saturate(1.2);
  -webkit-backdrop-filter: blur(24px) saturate(1.2);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
  padding: 6px;
  animation: glassSelectIn 180ms ease;

  @keyframes glassSelectIn {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Custom scrollbar for dark theme */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
  }
`;

const DropdownItem = styled.button<{ $active?: boolean; $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border: none;
  border-radius: 10px;
  background: ${({ $active, $highlighted }) =>
    $active
      ? "rgba(205, 128, 84, 0.18)"
      : $highlighted
        ? "rgba(255, 255, 255, 0.08)"
        : "transparent"};
  font-family: ${entryFont.body};
  font-size: 16px;
  color: ${({ $active }) =>
    $active ? entryColors.highlight : entryColors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease;

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(205, 128, 84, 0.22)" : "rgba(255, 255, 255, 0.08)"};
  }
`;

const DropdownCheckmark = styled.span`
  flex-shrink: 0;
  margin-left: auto;
  padding-left: 12px;
  display: inline-flex;
`;

export type GlassSelectProps = {
  label: string;
  placeholder?: string;
  items: string[];
  selectedIndex?: number;
  onSelect: (index: number) => void;
};

export const GlassSelect = ({
  label,
  placeholder,
  items,
  selectedIndex,
  onSelect,
}: GlassSelectProps) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const hasValue = selectedIndex != null && selectedIndex >= 0;
  const displayText = hasValue ? items[selectedIndex] : placeholder ?? "";

  const close = useCallback(() => {
    setOpen(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, close]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
        setHighlightedIndex(hasValue ? selectedIndex : 0);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, items.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (highlightedIndex >= 0) {
          onSelect(highlightedIndex);
          close();
        }
        break;
      case "Escape":
        e.preventDefault();
        close();
        break;
    }
  };

  useEffect(() => {
    if (!open || highlightedIndex < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open]);

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <SelectTrigger
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        $open={open}
        $hasValue={hasValue}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) setHighlightedIndex(hasValue ? selectedIndex : 0);
        }}
        onKeyDown={handleKeyDown}
      >
        <SelectLabel $floated={hasValue}>{label}</SelectLabel>
        {hasValue ? <SelectValue>{displayText}</SelectValue> : null}
        <SelectChevron $open={open}>
          <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden="true">
            <path
              d="M1 1L7 7L13 1"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </SelectChevron>
      </SelectTrigger>

      {open && (
        <DropdownPanel ref={listRef} role="listbox">
          {items.map((item, index) => (
            <DropdownItem
              key={item}
              type="button"
              role="option"
              aria-selected={index === selectedIndex}
              $active={index === selectedIndex}
              $highlighted={index === highlightedIndex}
              onClick={() => {
                onSelect(index);
                close();
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {item}
              {index === selectedIndex && (
                <DropdownCheckmark>
                  <svg width="14" height="11" viewBox="0 0 12 10" fill="none" aria-hidden="true">
                    <path
                      d="M1 5.2L4.2 8.4L11 1.6"
                      stroke={entryColors.highlight}
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </DropdownCheckmark>
              )}
            </DropdownItem>
          ))}
        </DropdownPanel>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Misc                                                                */
/* ------------------------------------------------------------------ */

export const BackArrowIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M15 5L8 12L15 19"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const FormColumn = styled.div`
  width: 100%;
  max-width: var(--entry-form-width, ${entryLayout.formWidth}px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

/* ------------------------------------------------------------------ */
/* Glass selection option (port of mobile GlassSelectionOption)        */
/* ------------------------------------------------------------------ */

const GlassOptionCard = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  overflow: hidden;
  padding: 6px 12px 6px 16px;
  border-radius: 10px;
  background: ${({ $selected }) =>
    $selected ? "rgba(205, 128, 84, 0.1)" : "rgba(244, 241, 242, 0.05)"};
  border: 2px solid ${({ $selected }) => ($selected ? "rgba(205, 128, 84, 0.8)" : "transparent")};
  box-shadow: ${({ $selected }) => ($selected ? "0 0 24px rgba(205, 128, 84, 0.5)" : "none")};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  cursor: pointer;
  transition:
    background 180ms ease,
    border-color 180ms ease,
    box-shadow 180ms ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? "rgba(205, 128, 84, 0.14)" : "rgba(244, 241, 242, 0.1)"};
  }

  &:focus-visible {
    outline: 2px solid ${entryColors.highlight};
    outline-offset: 2px;
  }
`;

const GlassOptionBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 14px 0;
`;

const GlassOptionTitle = styled.div`
  font-family: ${entryFont.body};
  font-size: 16px;
  font-weight: 500;
  color: ${entryColors.textPrimary};

  em {
    font-family: ${entryFont.title};
    font-style: italic;
    font-weight: 500;
    color: #ffac7d;
  }
`;

const GlassOptionSubtitle = styled.div`
  margin-top: 2px;
  font-family: ${entryFont.body};
  font-size: 13px;
  color: rgba(244, 241, 242, 0.6);
`;

const GlassOptionIndicator = styled.span<{ $selected?: boolean }>`
  flex-shrink: 0;
  margin-left: 20px;
  margin-right: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: ${({ $selected }) => ($selected ? "none" : "1px solid rgba(255, 255, 255, 0.2)")};
  background: ${({ $selected }) => ($selected ? entryColors.terracotta : "transparent")};
  transition: background 180ms ease;
`;

const GlassOptionImage = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  img,
  svg {
    display: block;
    max-height: 104px;
    width: auto;
  }
`;

const IndicatorCheck = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none" aria-hidden="true">
    <path
      d="M1 5.2L4.2 8.4L11 1.6"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type EntryGlassOptionProps = {
  title: string;
  /** Portion of the title rendered in italic serif peach, like mobile. */
  highlightText?: string;
  subtitle?: string;
  selected: boolean;
  onSelect: () => void;
  rightElement?: ReactNode;
  leftElement?: ReactNode;
  /** Mobile hides the checkbox circle; selection reads from the copper border. */
  hideIndicator?: boolean;
};

export const EntryGlassOption = ({
  title,
  highlightText,
  subtitle,
  selected,
  onSelect,
  rightElement,
  leftElement,
  hideIndicator = false,
}: EntryGlassOptionProps) => {
  const renderTitle = () => {
    if (!highlightText || !title.includes(highlightText)) return title;
    const [before, after] = title.split(highlightText);
    return (
      <>
        {before}
        <em>{highlightText}</em>
        {after}
      </>
    );
  };

  return (
    <GlassOptionCard
      type="button"
      role="checkbox"
      aria-checked={selected}
      $selected={selected}
      onClick={onSelect}
    >
      {leftElement ? (
        <GlassOptionImage style={{ marginRight: 16 }}>{leftElement}</GlassOptionImage>
      ) : null}
      <GlassOptionBody>
        <GlassOptionTitle>{renderTitle()}</GlassOptionTitle>
        {subtitle ? <GlassOptionSubtitle>{subtitle}</GlassOptionSubtitle> : null}
      </GlassOptionBody>
      {!hideIndicator && (
        <GlassOptionIndicator $selected={selected}>
          {selected ? <IndicatorCheck /> : null}
        </GlassOptionIndicator>
      )}
      {rightElement ? <GlassOptionImage>{rightElement}</GlassOptionImage> : null}
    </GlassOptionCard>
  );
};
